import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Loader } from "@/components/loader/Loader";

interface AjaxParamConfirmationProps {
  toolName: string;
  params: Record<string, any>;
  apiEndpoint?: string;
  userEmail?: string;
  onLoadingChange?: (loading: boolean) => void;
}

/**
 * Component that fetches and displays human-readable parameter information
 * from the backend, replacing raw record IDs with meaningful data.
 */
export function AjaxParamConfirmation({
  toolName,
  params,
  apiEndpoint,
  userEmail,
  onLoadingChange,
}: AjaxParamConfirmationProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayData, setDisplayData] = useState<Record<string, any> | null>(
    null
  );
  const isFetchingRef = useRef(false);
  const lastFetchKeyRef = useRef<string>("");

  // Memoize the callback to prevent parent re-renders
  const handleLoadingChange = useCallback(
    (isLoading: boolean) => {
      onLoadingChange?.(isLoading);
    },
    [onLoadingChange]
  );

  // Notify parent of loading state changes
  useEffect(() => {
    handleLoadingChange(loading);
  }, [loading, handleLoadingChange]);

  // Stringify params to avoid unnecessary re-fetches when object reference changes
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);
  const userEmailKey = userEmail || "";
  const fetchKey = useMemo(
    () =>
      `${toolName}-${apiEndpoint || "default"}-${paramsKey}-${userEmailKey}`,
    [toolName, apiEndpoint, paramsKey, userEmailKey]
  );

  useEffect(() => {
    // Prevent duplicate fetches
    if (isFetchingRef.current || lastFetchKeyRef.current === fetchKey) {
      return;
    }

    const fetchDisplayData = async () => {
      isFetchingRef.current = true;
      lastFetchKeyRef.current = fetchKey;

      try {
        setLoading(true);
        setError(null);

        // Default endpoint based on tool name if not provided
        const endpoint = apiEndpoint || `/tool-params/${toolName}`;

        // Add userEmail to params if provided
        const requestParams = userEmail ? { ...params, userEmail } : params;

        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestParams),
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch parameter details: ${response.statusText}`
          );
        }

        const data = await response.json();
        setDisplayData(data as Record<string, any>);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error occurred");
        // Fallback to showing raw params on error
        setDisplayData(params);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchDisplayData();
  }, [fetchKey, toolName, apiEndpoint, params, userEmail]);

  if (loading) {
    return (
      <div className="bg-background/80 p-2 pl-0 rounded-md text-sm flex items-center gap-2">
        <Loader size={16} />
        <span className="text-muted-foreground">Gathering details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background/80 p-2 pl-0 rounded-md text-sm">
        <span className="text-destructive text-xs">Error: {error}</span>
        <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words max-w-[450px]">
          {JSON.stringify(params, null, 2)}
        </pre>
      </div>
    );
  }

  return (
    <div className="bg-background/80 p-2 pl-0 rounded-md text-sm overflow-auto whitespace-pre-wrap break-words max-w-[450px]">
      {displayData && Object.keys(displayData).length > 0 ? (
        Object.entries(displayData).map(([key, value]) => (
          <div key={key} className="mb-2 last:mb-0">
            <span className="font-medium text-muted-foreground">{key}: </span>
            <span>
              {typeof value === "object" && value !== null
                ? JSON.stringify(value, null, 2)
                : String(value)}
            </span>
          </div>
        ))
      ) : (
        <span className="text-muted-foreground">No details available</span>
      )}
    </div>
  );
}
